import React from 'react';
import { TaskyInput, TaskyLabel, TaskySelect, TaskyTextarea } from '../../../components/tasky';
import { APPEAL_TYPE_LABELS, AppealType, DCM_APPEAL_AXIS_OPTIONS, DcmAppeal } from '../../../types/knowledge';

/** 訴求タイプの選択肢（編集フォーム用）。DCM訴求軸の候補が定義されている9タイプのみを対象にする
 *  （functional/valueはfeature/benefitの旧エイリアスのため選択肢からは除外）。 */
const APPEAL_TYPE_OPTIONS = Object.keys(DCM_APPEAL_AXIS_OPTIONS) as AppealType[];

/**
 * 訴求タイプ → 色 / 背景 / アイコン（Material Icons Round）。
 * DCMモック(02 §11)の TYPE_META 準拠。functional/value は feature/benefit と同系色に寄せる。
 */
const APPEAL_TYPE_META: Record<AppealType, { icon: string; color: string; bg: string }> = {
  feature: { icon: 'tune', color: '#616FC9', bg: 'rgba(97,111,201,.10)' },
  functional: { icon: 'tune', color: '#616FC9', bg: 'rgba(97,111,201,.10)' },
  merit: { icon: 'trending_up', color: '#4F86C6', bg: 'rgba(79,134,198,.10)' },
  benefit: { icon: 'redeem', color: '#44C2A4', bg: 'rgba(68,194,164,.12)' },
  value: { icon: 'redeem', color: '#44C2A4', bg: 'rgba(68,194,164,.12)' },
  trust: { icon: 'verified', color: '#9675C9', bg: 'rgba(150,117,201,.10)' },
  emotion: { icon: 'favorite', color: '#C9619C', bg: 'rgba(201,97,156,.10)' },
  negative: { icon: 'report_problem', color: '#C9A061', bg: 'rgba(201,160,97,.13)' },
  offer: { icon: 'local_offer', color: '#E0894B', bg: 'rgba(224,137,75,.13)' },
  comparison: { icon: 'compare_arrows', color: '#5BA8C4', bg: 'rgba(91,168,196,.12)' },
  social: { icon: 'eco', color: '#6FA86F', bg: 'rgba(111,168,111,.13)' },
};

const CARD_SHADOW = '0 0 18px 0 rgba(31,23,18,.06)';
const FOCUS_SHADOW = `0 0 0 2px #616FC9, ${CARD_SHADOW}`;
const GOLD = '#A87C2B';

/** 訴求の個別編集で書き換え可能なフィールド（コピー案本文以外の単一値フィールド）。 */
type AppealEditableField = 'name' | 'type' | 'appeal_axis' | 'direction';

interface AppealGenerateCardProps {
  appeal: DcmAppeal;
  selected?: boolean;
  isEditing?: boolean;
  /** 再生成中のコピーID集合（このカード内外を問わず、現在ローディング表示すべき行） */
  regeneratingCopyIds?: Set<string>;
  onSelect?: () => void;
  onToggleCopy?: (appealId: string, copyId: string) => void;
  /** 「使わない案を再生成」: この訴求の未選択コピーを全件再生成する */
  onRegenerateUnusedCopies?: (appealId: string) => void;
  onEditCopy?: (appealId: string, copyId: string, text: string) => void;
  onDeleteCopy?: (appealId: string, copyId: string) => void;
  /** 訴求名・訴求タイプ・訴求軸・狙いの個別編集（編集モード中のみ呼ばれる） */
  onEditField?: (appealId: string, field: AppealEditableField, value: string) => void;
  /** 空のコピー案行を1件追加する（編集モード中のみ呼ばれる） */
  onAddCopy?: (appealId: string) => void;
  onDeleteAppeal?: (appealId: string) => void;
}

const AppealGenerateCard: React.FC<AppealGenerateCardProps> = ({
  appeal,
  selected = false,
  isEditing = false,
  regeneratingCopyIds,
  onSelect,
  onToggleCopy,
  onRegenerateUnusedCopies,
  onEditCopy,
  onDeleteCopy,
  onEditField,
  onAddCopy,
  onDeleteAppeal,
}) => {
  const meta = APPEAL_TYPE_META[appeal.type] || APPEAL_TYPE_META.feature;
  const copies = appeal.copies || [];
  const useCount = copies.filter((copy) => copy.use).length;
  const regenCount = copies.length - useCount;
  // このカード内のいずれかのコピーが再生成中か（ボタン活性/ローディング表示に使う）
  const isRegenerating = copies.some((copy) => regeneratingCopyIds?.has(copy.id));

  return (
    <div
      className={`bg-white rounded-2xl p-5${selected ? ' appeal-focus' : ''}`}
      style={{ boxShadow: selected ? FOCUS_SHADOW : CARD_SHADOW }}
      onClick={onSelect}
      data-testid="appeal-card"
    >
      {/* ヘッダー: タイプアイコン ＋ バッジ（訴求タイプ / 訴求軸）＋ 訴求名 ＋ 削除（編集時のみ） */}
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
          style={{ background: meta.bg, color: meta.color }}
        >
          <span className="material-icons-round text-[17px]">{meta.icon}</span>
        </span>
        {isEditing ? (
          // 個別編集: タイプ（選択式）／軸／名前をインライン編集フォームに差し替える。
          <div className="min-w-0 flex-1 space-y-2" onClick={(event) => event.stopPropagation()}>
            <div className="flex gap-2">
              <div className="w-[152px] flex-shrink-0">
                <TaskyLabel className="mb-0.5 block text-[10px] font-normal text-gray-400">訴求タイプ</TaskyLabel>
                <TaskySelect
                  value={appeal.type}
                  aria-label="訴求タイプ"
                  className="h-8 py-0 text-[12px]"
                  onChange={(event) => onEditField?.(appeal.appeal_id, 'type', event.target.value)}
                >
                  {APPEAL_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {APPEAL_TYPE_LABELS[type]}
                    </option>
                  ))}
                </TaskySelect>
              </div>
              <div className="min-w-0 flex-1">
                <TaskyLabel className="mb-0.5 block text-[10px] font-normal text-gray-400">訴求軸</TaskyLabel>
                <TaskyInput
                  value={appeal.appeal_axis || ''}
                  aria-label="訴求軸"
                  className="h-8 text-[12px]"
                  placeholder="例: 基本性能"
                  onChange={(event) => onEditField?.(appeal.appeal_id, 'appeal_axis', event.target.value)}
                />
              </div>
            </div>
            <div>
              <TaskyLabel className="mb-0.5 block text-[10px] font-normal text-gray-400">訴求名</TaskyLabel>
              <TaskyInput
                value={appeal.name}
                aria-label="訴求名"
                className="h-8 text-[13px] font-bold"
                onChange={(event) => onEditField?.(appeal.appeal_id, 'name', event.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: meta.bg, color: meta.color }}
              >
                訴求タイプ: {APPEAL_TYPE_LABELS[appeal.type] || '訴求'}
              </span>
              {/* ⑧: 訴求タイプのバッジの横に訴求軸も並べて表示する（appeal_axis 未設定時もバッジ自体は出す） */}
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                style={{ background: 'rgba(201,160,97,.14)', color: GOLD }}
              >
                <span className="material-icons-round text-[12px]">sell</span>
                訴求軸: {appeal.appeal_axis || '未設定'}
              </span>
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="flex-shrink-0 text-[10px] font-bold text-gray-400">訴求名</span>
              <span className="text-[15px] font-bold leading-snug text-dark">{appeal.name}</span>
            </div>
          </div>
        )}
        {isEditing && (
          <button
            type="button"
            className="flex-shrink-0 text-gray-400 transition-colors hover:text-destructive"
            title="この訴求を削除"
            onClick={(event) => {
              event.stopPropagation();
              onDeleteAppeal?.(appeal.appeal_id);
            }}
          >
            <span className="material-icons-round text-[18px]">delete_outline</span>
          </button>
        )}
      </div>

      {/* この訴求の狙い（編集中は常に表示し、未入力からでも追記できるようにする） */}
      {isEditing ? (
        <div className="mt-3 rounded-xl bg-[#F4F3EF] px-3.5 py-2.5" onClick={(event) => event.stopPropagation()}>
          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold text-gray-400">
            <span className="material-icons-round text-[13px]">psychology</span>
            この訴求の狙い
          </div>
          <TaskyTextarea
            value={appeal.direction || ''}
            aria-label="この訴求の狙い"
            rows={2}
            className="text-[12.5px]"
            placeholder="この訴求の狙いを記述してください"
            onChange={(event) => onEditField?.(appeal.appeal_id, 'direction', event.target.value)}
          />
        </div>
      ) : (
        (appeal.hook || appeal.direction) && (
          <div className="mt-3 rounded-xl bg-[#F4F3EF] px-3.5 py-2.5">
            <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold text-gray-400">
              <span className="material-icons-round text-[13px]">psychology</span>
              この訴求の狙い
            </div>
            <p className="text-[12.5px] leading-relaxed text-gray-700">
              {appeal.hook ? (
                <>
                  「<b className="font-bold text-destructive">{appeal.hook}</b>」に、{appeal.direction}
                </>
              ) : (
                appeal.direction
              )}
            </p>
          </div>
        )
      )}

      {/* コピー案ヘッダー */}
      <div className="mb-2 mt-4 flex flex-wrap items-center gap-2">
        <span className="material-icons-round text-[14px] text-gray-400">edit_note</span>
        <span className="text-[11px] font-bold text-gray-500">コピー案 {copies.length}件</span>
        <span className="text-[10px] font-semibold text-primary">使う {useCount}</span>
        <span className="text-[10px] font-semibold" style={{ color: GOLD }}>
          再生成する案 {regenCount}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold text-gray-500 transition-colors hover:bg-warm-surface disabled:cursor-not-allowed disabled:opacity-40"
          // 編集中（訴求の個別編集）は再生成を止める。非同期のAI応答で編集中のフィールドが
          // 差し替わるのを防ぐため、保存またはキャンセルするまで無効化する。
          disabled={regenCount === 0 || isRegenerating || isEditing}
          title={isEditing ? '編集中は使用できません（保存後にご利用ください）' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            if (regenCount > 0 && !isRegenerating && !isEditing) {
              onRegenerateUnusedCopies?.(appeal.appeal_id);
            }
          }}
        >
          <span className={`material-icons-round text-[13px]${isRegenerating ? ' animate-spin' : ''}`}>
            {isRegenerating ? 'sync' : 'autorenew'}
          </span>
          {isRegenerating ? '再生成中…' : '使わない案を再生成'}
        </button>
      </div>

      {/* コピー行（全件） */}
      <div className="space-y-1.5">
        {copies.map((copy) => {
          const copyRegenerating = regeneratingCopyIds?.has(copy.id) ?? false;
          return (
          <div
            key={copy.id}
            className="flex items-start gap-[9px] rounded-[11px] px-3 py-2 transition-[background] duration-[120ms]"
            style={{ background: copy.use ? '#EEF0FA' : '#FAFAF8', opacity: copyRegenerating ? 0.55 : 1 }}
          >
            <button
              type="button"
              className={`mt-px flex flex-shrink-0 cursor-pointer border-none bg-transparent p-0 ${
                copy.use ? 'text-primary' : 'text-[#c4c0b4]'
              }`}
              title={copy.use ? '使う（クリックで外す）' : '使わない＝再生成する案（クリックで使う）'}
              disabled={copyRegenerating}
              onClick={(event) => {
                event.stopPropagation();
                onToggleCopy?.(appeal.appeal_id, copy.id);
              }}
            >
              <span
                className="material-icons-round"
                style={{ fontSize: '18px', color: copy.use ? '#616FC9' : '#C4C0B4' }}
              >
                {copy.use ? 'check_box' : 'check_box_outline_blank'}
              </span>
            </button>
            <span
              className={`flex-1 text-[13px] leading-relaxed ${copy.use ? 'text-gray-700' : 'text-gray-400'}`}
              contentEditable={isEditing}
              suppressContentEditableWarning
              onClick={(event) => {
                if (isEditing) event.stopPropagation();
              }}
              onBlur={
                isEditing
                  ? (event) => onEditCopy?.(appeal.appeal_id, copy.id, event.currentTarget.innerText.trim())
                  : undefined
              }
              onKeyDown={
                isEditing
                  ? (event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        (event.currentTarget as HTMLElement).blur();
                      }
                    }
                  : undefined
              }
            >
              {copy.text}
            </span>
            {copy.use ? (
              <span
                className="mt-1 flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold text-primary"
                style={{ background: 'rgba(97,111,201,.1)' }}
              >
                使う
              </span>
            ) : (
              <span
                className="mt-1 flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold"
                style={{ background: 'rgba(201,160,97,.14)', color: GOLD }}
              >
                再生成する案
              </span>
            )}
            {isEditing && (
              <button
                type="button"
                className="mt-0.5 flex-shrink-0 text-gray-400 transition-colors hover:text-destructive"
                title="このコピー案を削除"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteCopy?.(appeal.appeal_id, copy.id);
                }}
              >
                <span className="material-icons-round text-[15px]">close</span>
              </button>
            )}
          </div>
          );
        })}
        {isEditing && (
          <button
            type="button"
            className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] font-semibold text-gray-500 transition-colors hover:bg-warm-surface hover:text-primary"
            onClick={(event) => {
              event.stopPropagation();
              onAddCopy?.(appeal.appeal_id);
            }}
          >
            <span className="material-icons-round text-[14px]">add</span>
            コピー案を追加
          </button>
        )}
      </div>
    </div>
  );
};

export default AppealGenerateCard;
